import { useContext } from 'react';

import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';

import { SchoolContext } from 'context/SchoolContext';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  canonical?: string;
  ogType?: 'website' | 'article' | 'product' | 'business.business';
  ogImage?: string;
  ogImageAlt?: string;
  structuredData?: object | object[];
  noIndex?: boolean;
  noFollow?: boolean;
}

export function SEO({
  title,
  description,
  keywords = [],
  canonical,
  ogType = 'website',
  ogImage,
  ogImageAlt,
  structuredData,
  noIndex = false,
  noFollow = false,
}: SEOProps): JSX.Element {
  const schoolContext = useContext(SchoolContext);
  const location = useLocation();

  // Build complete title with school name
  const fullTitle = title
    ? `${title} | ${schoolContext?.name || schoolContext?.shortName || 'Driver Education'}`
    : `Driver Education - ${schoolContext?.name || schoolContext?.shortName || 'Driving School'}`;

  // Use provided description or fallback to school mission statement
  const metaDescription =
    description || schoolContext?.missionStatement || 'Professional driver education and training services.';

  // Build canonical URL
  const currentUrl = `https://${window.location.hostname}${location.pathname}`;
  const canonicalUrl = canonical || currentUrl;

  // Determine Open Graph image
  const ogImageUrl = ogImage || schoolContext?.logoImage || '/dd-logo.png';
  const fullOgImageUrl = ogImageUrl.startsWith('http')
    ? ogImageUrl
    : `https://${window.location.hostname}${ogImageUrl}`;

  // Build keywords string
  const keywordsString =
    keywords.length > 0
      ? keywords.join(', ')
      : `driver education, driving school, driving lessons, ${schoolContext?.name || 'driving training'}`;

  // Build robots meta content
  const robotsContent = `${noIndex ? 'noindex' : 'index'},${noFollow ? 'nofollow' : 'follow'}`;

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={keywordsString} />
      <meta name="robots" content={robotsContent} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={fullOgImageUrl} />
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      <meta property="og:site_name" content={schoolContext?.name || 'Driver Education'} />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={fullOgImageUrl} />
      {ogImageAlt && <meta name="twitter:image:alt" content={ogImageAlt} />}

      {/* Structured data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(structuredData) ? structuredData : [structuredData])}
        </script>
      )}
    </Helmet>
  );
}

export default SEO;
